import clsx from "clsx";
import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { closeAssistedSearch } from "web/redux/ducks/app-header-bar";
import {
    hideMainNavigation,
    setSelectedGender,
    showMainNavigation,
} from "web/redux/ducks/main-navigation";
import { ReduxStoreState } from "web/redux/store";
import analytics from "web/script/analytics/analytics";
import { gettext } from "web/script/modules/django-i18n";
import { AppHeaderBarGenderSerializer } from "web/types/serializers";
import styles from "./header-genders.module.css";

const MENU_OPENED = "opened";
const MENU_CLOSED = "closed";

function extractSelectedGender(state: ReduxStoreState): string {
    return state.mainNavigationReducer.selectedGender;
}

function extractMenuOpen(state: ReduxStoreState): boolean {
    return state.mainNavigationReducer.visible;
}

interface HeaderGendersProps {
    genders: AppHeaderBarGenderSerializer[];
}

function HeaderGenders({ genders }: HeaderGendersProps): React.ReactElement {
    const activeSelected = useSelector(extractSelectedGender);
    const isMenuOpen = useSelector(extractMenuOpen);
    const dispatch = useDispatch();

    // Update the redux state with the navbar gender if it's not set
    useEffect(() => {
        if (!activeSelected) {
            genders.forEach((gender) => {
                if (gender.selected) {
                    dispatch(setSelectedGender(gender.value));
                }
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    function sendMenuAnalytics(menuState, targetGender): void {
        analytics.event("nav", menuState, `nav_${targetGender.toLowerCase()}_menu`);
    }

    function onGenderClick(event: React.SyntheticEvent<HTMLAnchorElement>, gender = "women"): void {
        event.preventDefault();
        const switchGender = gender !== activeSelected;

        if (!switchGender && isMenuOpen) {
            dispatch(hideMainNavigation());
            sendMenuAnalytics(MENU_CLOSED, gender);
        } else {
            dispatch(closeAssistedSearch());
            dispatch(setSelectedGender(gender));
            dispatch(showMainNavigation());
            sendMenuAnalytics(MENU_OPENED, gender);
        }
    }

    return (
        <span className={clsx(styles.genders, "hidden-tablet")}>
            {genders.map((gender) => (
                <a
                    key={gender.value}
                    href={`${gender.gender_home_page}#category-navigation`}
                    className={clsx(styles.gender, {
                        [styles.genderSelected]: activeSelected
                            ? activeSelected === gender.value
                            : gender.selected,
                    })}
                    onClick={(event) => onGenderClick(event, gender.value)}
                >
                    <span>
                        {gender.value === "women" && gettext("header.gender.women").toUpperCase()}
                        {gender.value === "men" && gettext("header.gender.men").toUpperCase()}
                    </span>
                </a>
            ))}
        </span>
    );
}

export default HeaderGenders;
